import {
  FormProvider as FormContextProvider,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form';
import { forwardRef } from 'react';

import type { FormEvent, ReactElement, ReactNode, RefAttributes } from 'react';

export type FormProviderBaseProps = {
  id?: string;
  /**
   * callback to be executed when submitting the form
   */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  className?: string;
};

export type FormProviderProps<D extends FieldValues = FieldValues> = UseFormReturn<D> &
  FormProviderBaseProps;

export type FormProvider = {
  <D extends FieldValues>(
    props: FormProviderProps<D> & RefAttributes<HTMLFormElement>,
  ): ReactElement | null;
};

/**
 * A wrapper around {@link https://react-hook-form.com/api/useformcontext|React Hook Form's FormProvider}
 */
export const FormProvider = forwardRef<HTMLFormElement, FormProviderProps>(function FormProvider(
  props,
  ref,
) {
  const { children, className, onSubmit, id, ...methods } = props;

  return (
    <FormContextProvider {...methods}>
      <form
        id={id}
        ref={ref}
        className={className}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.(e);
        }}
      >
        {children}
      </form>
    </FormContextProvider>
  );
}) as FormProvider;
